import React, { memo, useState, useMemo, useContext } from "react";
import View from "./view";
import isempty from "lodash/isEmpty";
import PT from "prop-types";
import MasterSelector from "./MasterSelector";
import { ServiceAPI } from "@/api/service";
import { MasterAPI } from "@/api/master";
import { reduceServiceDiscounts, Money } from "@/utils/money";
import { ComponentSelectors as BasketSelectors } from "@/store/basket/selectors";
import Context from "../context";
import Animated from "_c/Animated";
import DiscountEditor from "_c/DiscountEditor";
import { useToggle } from "@/hooks/useToggle";
import { useMut } from "@/hooks/useMut";
import { MasterServicesService } from "@/services/masterServices";
import { useQuery } from "react-query";
import { MasterServicesCacheService } from "@/services/masterServices/cache";
import { MasterDiscountsCacheService } from "@/services/masterDiscounts/cache";
import { MasterDiscountsService } from "@/services/masterDiscounts";

const SaloonService = (props) => {
  const {
    authMasterId,
    authUserId,
    addToBasket,
    t,
    openServiceEditor,
    isOwner,
    canEditServices,
  } = useContext(Context);
  const [isDiscountEditorOpen, toggleDiscountEditor] = useToggle(false);
  const [selectedMasterId, setSelectedMasterId] = useState(null);
  const selectedMaster = useMemo(() => {
    return props.masters.find(({ _id }) => _id === selectedMasterId);
  }, [selectedMasterId, props.masters]);
  const selectedServiceId = selectedMaster?.service;

  const isQueriesEnabled = !!selectedMasterId && !!selectedServiceId;
  const serviceQuery = useQuery(
    MasterServicesCacheService.buildByIdQueryKey(selectedServiceId),
    () => ServiceAPI.findById(selectedServiceId).then((d) => d.data),
    {
      enabled: isQueriesEnabled,
    }
  );
  const serviceQueryData = serviceQuery.data;
  const discountsQuery = useQuery(
    MasterDiscountsCacheService.buildByMasterQueryKey(selectedMasterId),
    () => MasterAPI.getDiscounts(selectedMasterId).then((d) => d.data),
    {
      select: (list) => {
        if (!list) return list;
        return list.reduce((acc, d) => {
          if (d.client) return acc;
          if (d.service && d.service !== selectedServiceId) return acc;
          acc.push(d);
          return acc;
        }, []);
      },
      enabled: isQueriesEnabled,
    }
  );
  const discountsQueryData = discountsQuery.data;
  const service = useMemo(() => {
    if (!serviceQueryData || !discountsQueryData) return null;
    return {
      ...serviceQueryData,
      discounts: discountsQueryData,
    };
  }, [serviceQueryData, discountsQueryData]);

  const activeDiscount = useMemo(() => {
    if (!service || isempty(service.discounts)) return null;
    return reduceServiceDiscounts({
      discounts: service.discounts,
      userId: authUserId,
    });
  }, [service, authUserId]);
  const finalPrice = useMemo(() => {
    if (!service) return null;
    if (activeDiscount) {
      return Money.subtractPercent(service.price, activeDiscount.amount);
    }
    return service.price;
  }, [service, activeDiscount]);
  const onOpenEditor = () => {
    openServiceEditor({ master: selectedMaster._id, service });
  };
  const onBasketAdd = (amount) => {
    addToBasket({
      service,
      amount: service.per_unit ? amount : undefined,
      discount: activeDiscount,
    });
  };
  const canAddToBasket = useMemo(() => {
    return (
      service &&
      service.master !== authMasterId &&
      BasketSelectors.canAddToBasket(props.basket, service.master)
    );
  }, [authMasterId, service, props.basket]);
  const isInBasket = useMemo(() => {
    if (service) {
      return BasketSelectors.isInBasket(props.basket, service._id);
    }
    return false;
  }, [props.basket, service]);
  const createDiscount = (data) => MasterDiscountsService.createDiscount(data);
  const updateDiscount = (id, data) =>
    MasterDiscountsService.updateDiscount(id, data);
  const deleteDiscount = (id) => MasterDiscountsService.removeDiscount(id);
  const removeMut = useMut(
    () => {
      return MasterServicesService.deleteService(service);
    },
    {
      t,
      notifyOnError: true,
      confirm: true,
    }
  );
  return (
    <>
      <View
        t={t}
        title={props.title}
        MasterSelector={
          <MasterSelector
            selectedMaster={selectedMasterId}
            masters={props.masters}
            selectMaster={setSelectedMasterId}
          />
        }
        isInBasket={isInBasket}
        canAddToBasket={canAddToBasket}
        removeMut={removeMut}
        addToBasket={onBasketAdd}
        service={service}
        oldPrice={activeDiscount ? service.price : null}
        finalPrice={finalPrice}
        canEditServices={canEditServices}
        isOwner={isOwner}
        openEditor={onOpenEditor}
        avgPrice={props.avgPrice}
        toggleDiscountEditor={toggleDiscountEditor}
        selectedMaster={selectedMaster}
      />
      {isOwner && service && selectedMasterId && (
        <Animated visible={isDiscountEditorOpen}>
          <DiscountEditor
            masterId={selectedMasterId}
            onClose={toggleDiscountEditor}
            discounts={service.discounts}
            service={service._id}
            t={t}
            deleteDiscount={deleteDiscount}
            updateDiscount={updateDiscount}
            disableClientPicker
            createDiscount={createDiscount}
          />
        </Animated>
      )}
    </>
  );
};
SaloonService.displayName = "SaloonService";
SaloonService.propTypes = {
  title: PT.string,
  masters: PT.array,
  avgPrice: PT.object,
  basket: PT.array,
};

export default memo(SaloonService);
